/**
 * Quality / Settings (design board 6), saved in the project's `.flow/quality.yaml`: "Use the recommended set", when each
 * check runs (on every change, before Publish; nightly is coming), the rules that block Publish (switches and numbers),
 * the licence allow list, the online lookups (OSV, the package registries), the tests' time limit, the languages and
 * tools found in the code, and the ignore rules (each with a reason and an end date; secrets may have none). Viewers see
 * everything read-only. Edits wait in a Save / Cancel bar; a 400's errors show next to their field (by `path`). The
 * ignore rules are added and removed at once (their own routes).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { Switch } from 'antd';
import { Plus, Trash, X } from '@phosphor-icons/react';
import {
  addIgnore, applyRecommended, deleteIgnore, detect as detectTools, getSettings, saveSettings,
} from '../../services/QualityService';
import { IgnoreForm, Pill, State, SubHead } from './QualityCommon';
import QualityWaivers from './QualityWaivers';
import QualityExternalSettings from './QualityExternalSettings';
import {
  CODE_STATS_ROWS, GATE_ROWS, WHEN_ROWS, checkTitle, daysUntil, errorText, errorsByPath, formatDate, ruleOn, withRuleFlag, withRuleNumber, withRuleOn, withWhen,
} from './qualityModel';
import './QualitySettingsV2.css';

// Why each rule that can block Publish matters, in a line.
const RULE_WHY = {
  criticalVulnerabilities: 'A critical known vulnerability can be attacked as soon as the app is online.',
  highVulnerabilitiesWithFix: 'A serious vulnerability that already has a fix should not stay open for long.',
  failingTests: 'A failing test means something the team expects no longer works.',
  coverage: 'With too little of the code run by tests, passing tests prove little.',
  secrets: 'A key in the code can be copied by anyone who sees the code, and used.',
  lintErrors: 'Lint errors are usually real bugs, like a name that does not exist.',
  licences: 'Some licences oblige you to publish your code or forbid commercial use.',
  readiness: 'Something works in the preview only: outside it the app breaks.',
  accessibility: 'Serious issues stop people using a keyboard or a screen reader from using the app.',
  bundleBudget: 'Heavy pages load slowly, above all on phones and slow networks.',
  p95Budget: 'When the slowest requests take too long, people wait or give up.',
  privacyLogs: 'Logs are kept and read by many people: personal data must not end up there.',
  breakingApi: 'Apps and services that call this API stop working after the change.',
  provenance: 'Without a working signing key, or with an attestation that no longer verifies, nobody can prove what was released.',
  externalGate: 'Your organization\'s SonarQube quality gate failed, or the Semgrep AppSec Platform found something it blocks.',
  codeScanning: 'Semgrep or Hadolint found a serious problem in code or Dockerfiles that Flow\'s own rules do not cover.',
  lighthouse: 'A low score means slow, hard to use or hard to find pages.',
};
const A11Y_NOTE = 'Runs in your browser on the running preview (Run all checks and Publish start it)';
const A11Y_HEADLESS_NOTE = 'Runs in a headless browser in your Flow, on the running preview';

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const FieldError = ({ text }) => (text ? <span className="quality-field-error" role="alert">{text}</span> : null);
FieldError.propTypes = { text: PropTypes.string };

function untilCell(until) {
  if (!until) { return <span className="quality-muted">No end (secrets only)</span>; }
  const left = daysUntil(until);
  if (left !== null && left < 0) { return <Pill tone="fail">Expired {formatDate(until)}</Pill>; }
  if (left !== null && left <= 14) { return <Pill tone="warn">Ends in {left === 0 ? 'less than a day' : `${left} ${left === 1 ? 'day' : 'days'}`}</Pill>; }
  return formatDate(until);
}

const testsText = (s) => (s.tests?.framework ? `${s.tests.framework}${s.tests.coverage ? `, ${s.tests.coverage}` : ''}${s.tests.supported ? '' : ' (not supported yet)'}` : '');
const lintText = (s) => (s.lint?.tools?.length ? s.lint.tools.join(', ') : s.lint?.builtin ? 'Built-in (ESLint core rules)' : '–');
const TOOLS_COLUMNS = [
  { key: 'service', label: 'Service', width: 150, className: 'quality-mono', value: (s) => s.service },
  {
    key: 'languages',
    label: 'Languages',
    value: (s) => (s.languages || []).join(', ') || '–',
    title: (s) => `${(s.languages || []).join(', ') || '–'}${s.packageManager ? ` · ${s.packageManager}` : ''}`,
    render: (s) => <>{(s.languages || []).join(', ') || '–'}{s.packageManager ? <span className="quality-muted"> · {s.packageManager}</span> : null}</>,
  },
  { key: 'tests', label: 'Tests and coverage', width: 190, value: (s) => testsText(s) || null, title: (s) => testsText(s) || 'No tests found', render: (s) => testsText(s) || <span className="quality-muted">No tests found</span> },
  { key: 'lint', label: 'Lint and format', width: 190, value: lintText },
  { key: 'lockfiles', label: 'Lockfiles', width: 160, className: 'quality-mono quality-small', value: (s) => (s.lockfiles || []).join(', ') || '–' },
  { key: 'container', label: 'Container', width: 110, value: (s) => (s.dockerfile ? 'Dockerfile' : '–') },
];

// Each rule keeps its place in the draft (`index`): the table may show them sorted, Remove takes out the right one.
const IGNORE_COLUMNS = [
  { key: 'what', label: 'What', className: 'quality-mono quality-small', value: (rule) => rule.what },
  { key: 'check', label: 'Check', width: 140, value: (rule) => (rule.check ? checkTitle(rule.check) : 'Any') },
  { key: 'path', label: 'Where', width: 150, className: 'quality-mono quality-small', value: (rule) => rule.path || 'Everywhere' },
  { key: 'reason', label: 'Reason', width: 200, wrap: 2, value: (rule) => rule.reason },
  { key: 'by', label: 'By', width: 130, value: (rule) => rule.by || '–' },
  { key: 'until', label: 'Until', width: 120, value: (rule) => rule.until || null, render: (rule) => untilCell(rule.until) },
];

export default function QualitySettings({
  projectId, canEdit: canEditProp = false, onBack, onSaved = null, findingChoices = [], headless = null,
}) {
  const [answer, setAnswer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [detected, setDetected] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLicence, setNewLicence] = useState('');
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const next = await getSettings(projectId);
      setAnswer(next);
      setDraft(next.settings);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
  }, [projectId]);

  const runDetect = useCallback(async () => {
    setDetecting(true);
    try { setDetected(await detectTools(projectId)); } catch { setDetected((prev) => prev || { services: [], failed: true }); } finally { setDetecting(false); }
  }, [projectId]);

  useEffect(() => { load(); runDetect(); }, [load, runDetect]);

  const canEdit = Boolean(answer ? answer.canEdit ?? canEditProp : canEditProp);
  const dirty = Boolean(answer && draft && !same(draft, answer.settings));

  const applySaved = (next, message = null) => {
    setAnswer((prev) => ({ ...prev, ...next, settings: next.settings, source: next.source || 'file', errors: undefined }));
    setNotice(message);
    onSaved?.();
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      const next = await saveSettings(projectId, draft);
      applySaved(next, 'Saved.');
      setDraft(next.settings);
    } catch (e) {
      const byPath = errorsByPath(e);
      setFieldErrors(byPath);
      setSaveError(Object.keys(byPath).length ? 'Some values are not valid: see the fields marked below.' : errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const recommended = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const next = await applyRecommended(projectId);
      applySaved(next, 'The recommended set is in place.');
      setDraft(next.settings);
      setFieldErrors({});
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  // The ignore rules are saved at once; any other edit in progress stays in the draft.
  const keepIgnores = (next) => {
    setAnswer((prev) => ({ ...prev, settings: next.settings, source: 'file' }));
    setDraft((prev) => ({ ...prev, ignore: next.settings?.ignore || [] }));
    onSaved?.();
  };
  const addRule = async (rule) => {
    const next = await addIgnore(projectId, rule);
    keepIgnores(next);
    setAdding(false);
    setNotice('Rule added.');
  };
  const removeRule = async (index) => {
    try {
      keepIgnores(await deleteIgnore(projectId, index));
      setNotice('Rule removed.');
    } catch (e) {
      setSaveError(errorText(e));
    }
  };

  if (loadError && !answer) {
    return (
      <div className="quality-sub" data-testid="quality-settings">
        <SubHead onBack={onBack} title="Settings" />
        <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load the settings" tone="bad">{errorText(loadError)}</State>
      </div>
    );
  }
  if (!answer || !draft) {
    return <div className="quality-sub" data-testid="quality-settings"><SubHead onBack={onBack} title="Settings" /><div className="quality-state" role="status">Loading the settings…</div></div>;
  }

  const ro = !canEdit;
  const set = (fn) => { setDraft((prev) => fn(prev)); setNotice(null); };
  const allow = draft.licences?.allow || [];
  const addLicence = () => {
    const value = newLicence.trim();
    if (!value || allow.includes(value)) { setNewLicence(''); return; }
    set((prev) => ({ ...prev, licences: { ...(prev.licences || {}), allow: [...(prev.licences?.allow || []), value] } }));
    setNewLicence('');
  };
  const removeLicence = (value) => set((prev) => ({ ...prev, licences: { ...(prev.licences || {}), allow: (prev.licences?.allow || []).filter((l) => l !== value) } }));
  const errorAt = (prefix) => Object.entries(fieldErrors).filter(([path]) => path === prefix || path.startsWith(`${prefix}.`)).map(([, m]) => m).join(' ') || null;

  return (
    <div className="quality-sub" data-testid="quality-settings">
      <SubHead onBack={onBack} title="Settings">
        <span className="quality-muted quality-small">For this project · {canEdit ? 'you can change them' : 'owners and editors can change them'} · saved in <span className="quality-mono">{answer.path || '.flow/quality.yaml'}</span></span>
        <span className="quality-grow" />
        {canEdit && <button className="quality-btn" disabled={saving} onClick={recommended} type="button">Use the recommended set</button>}
      </SubHead>

      {ro && <p className="quality-banner is-info" data-testid="quality-settings-readonly">You can look at these settings; only the project&apos;s owners and editors can change them.</p>}
      {answer.source === 'invalid' && (
        <div className="quality-banner is-warn" role="alert">
          <strong>The settings file does not parse, so the checks use the defaults.</strong>
          {(answer.errors || []).map((e, i) => <span key={i}>{e.path ? `${e.path}: ` : ''}{e.message}</span>)}
        </div>
      )}
      {answer.source === 'default' && <p className="quality-muted quality-small">These are the defaults: saving writes them into the project.</p>}

      <section aria-labelledby="quality-when-title" className="quality-card">
        <div className="quality-pad-head"><h2 id="quality-when-title">When checks run</h2><p className="quality-muted">Fast checks on every change; slow ones before Publish. Manual runs are always possible.</p></div>
        <div className="quality-table-box is-flush">
          <table className="quality-table">
            <thead><tr><th scope="col">Check</th><th className="is-c" scope="col">On every change</th><th className="is-c" scope="col">Before Publish</th><th className="is-c" scope="col">Nightly</th><th scope="col">Typical time</th></tr></thead>
            <tbody>
              {WHEN_ROWS.map((row) => (headless?.available && row.browser ? { ...row, name: 'Accessibility', browser: false, headless: true } : row)).map((row) => (
                <tr className={row.soon ? 'is-soon' : undefined} data-testid={`quality-when-${row.id}`} key={row.id}>
                  <td>
                    {row.name}
                    {row.browser && <span className="quality-muted quality-small quality-when-note">{A11Y_NOTE}</span>}
                    {row.headless && <span className="quality-muted quality-small quality-when-note">{A11Y_HEADLESS_NOTE}</span>}
                  </td>
                  {row.soon ? (
                    <>
                      <td className="is-c"><input aria-label={`${row.name} on every change`} disabled type="checkbox" /></td>
                      <td className="is-c"><input aria-label={`${row.name} before Publish`} disabled type="checkbox" /></td>
                      <td className="is-c"><input aria-label={`${row.name} nightly`} disabled type="checkbox" /></td>
                      <td><Pill tone="info">Coming soon</Pill></td>
                    </>
                  ) : (
                    <>
                      <td className="is-c">
                        <input
                          aria-label={`${row.name} on every change`}
                          checked={!row.browser && Boolean(draft.when?.[row.id]?.change)}
                          disabled={ro || row.browser}
                          onChange={(e) => set((p) => withWhen(p, row.id, 'change', e.target.checked))}
                          title={row.browser ? 'It needs your browser open on the preview, so it cannot run by itself after every change' : undefined}
                          type="checkbox"
                        />
                      </td>
                      <td className="is-c"><input aria-label={`${row.name} before Publish`} checked={Boolean(draft.when?.[row.id]?.publish)} disabled={ro} onChange={(e) => set((p) => withWhen(p, row.id, 'publish', e.target.checked))} type="checkbox" /></td>
                      <td className="is-c"><input aria-label={`${row.name} nightly (coming soon)`} disabled title="Coming soon" type="checkbox" /></td>
                      <td className="quality-muted">{row.time}<FieldError text={errorAt(`when.${row.id}`)} /></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="quality-muted quality-small quality-pad-foot">Nightly runs are coming soon.</p>
      </section>

      <section aria-labelledby="quality-gate-rules-title" className="quality-card">
        <div className="quality-pad-head"><h2 id="quality-gate-rules-title">Block Publish when</h2><p className="quality-muted">These rules make the release gate. Anything else only warns.</p></div>
        {GATE_ROWS.map((row) => {
          if (row.soon) {
            return (
              <div className="quality-rule is-soon" data-testid={`quality-rule-${row.id}`} key={row.id}>
                <Switch aria-label={row.text} checked={false} disabled size="small" />
                <span className="quality-grow">{row.text}</span>
                <Pill tone="info">Coming soon</Pill>
              </div>
            );
          }
          const value = draft.gate?.[row.id];
          const on = ruleOn(value);
          const error = errorAt(`gate.${row.id}`);
          const label = row.number ? `${row.text} N ${row.number.unit}` : row.text;
          return (
            <div className="quality-rule" data-testid={`quality-rule-${row.id}`} key={row.id}>
              <Switch aria-label={label} checked={on} disabled={ro} onChange={(next) => set((p) => withRuleOn(p, row.id, next))} size="small" />
              <span className="quality-grow">
                {row.text}
                {row.number && (
                  <>
                    {' '}
                    <input
                      aria-invalid={Boolean(error)}
                      aria-label={row.number.label}
                      className="quality-input quality-num"
                      disabled={ro}
                      max={row.number.max}
                      min={row.number.min}
                      onChange={(e) => set((p) => withRuleNumber(p, row.id, row.number.key, e.target.value === '' ? '' : Number(e.target.value)))}
                      type="number"
                      value={value?.[row.number.key] ?? ''}
                    />
                    {' '}{row.number.unit}
                  </>
                )}
                {row.flag && (
                  <label className="quality-rule-flag">
                    <input
                      checked={Boolean(value?.[row.flag.key])}
                      disabled={ro}
                      onChange={(e) => set((p) => withRuleFlag(p, row.id, row.flag.key, e.target.checked))}
                      type="checkbox"
                    />
                    {row.flag.label}
                  </label>
                )}
                {RULE_WHY[row.id] && <span className="quality-muted quality-small quality-rule-why">{RULE_WHY[row.id]}</span>}
              </span>
              {!on && <span className="quality-muted quality-small">Off: warns only</span>}
              <FieldError text={error} />
            </div>
          );
        })}
        <div className="quality-rule is-note">Owners can publish anyway with a written reason; it is kept in the project&apos;s evidence and its Activity.</div>
      </section>

      <QualityExternalSettings errorAt={errorAt} external={draft.external || null} onChange={(external) => set((prev) => ({ ...prev, external }))} projectId={projectId} readOnly={ro} />

      <section aria-labelledby="quality-stats-title" className="quality-card" data-testid="quality-code-stats-limits">
        <div className="quality-pad-head"><h2 id="quality-stats-title">Code stats limits</h2><p className="quality-muted">Info only: over a limit the Code stats card asks for attention, it never blocks Publish. Leave a limit empty for none.</p></div>
        {CODE_STATS_ROWS.map((row) => {
          const value = draft.codeStats?.[row.key];
          const error = errorAt(`codeStats.${row.key}`);
          return (
            <div className="quality-rule" data-testid={`quality-stats-${row.key}`} key={row.key}>
              <span className="quality-grow">
                {row.text}{' '}
                <input
                  aria-invalid={Boolean(error)}
                  aria-label={`${row.text} N ${row.unit}`}
                  className="quality-input quality-num"
                  disabled={ro}
                  max={row.max}
                  min={row.min}
                  onChange={(e) => set((p) => ({ ...p, codeStats: { ...(p.codeStats || {}), [row.key]: e.target.value === '' ? null : Number(e.target.value) } }))}
                  placeholder="none"
                  type="number"
                  value={value ?? ''}
                />
                {' '}{row.unit}
              </span>
              <FieldError text={error} />
            </div>
          );
        })}
      </section>

      <section aria-labelledby="quality-allow-title" className="quality-card quality-pad">
        <h2 id="quality-allow-title">Licence allow list</h2>
        <p className="quality-muted">A package whose licence is not on this list warns, or blocks Publish when the rule above is on.</p>
        <ul aria-label="Allowed licences" className="quality-tags">
          {allow.map((l) => (
            <li key={l}>
              <span className="quality-mono">{l}</span>
              {!ro && <button aria-label={`Remove ${l}`} className="quality-tag-x" onClick={() => removeLicence(l)} type="button"><X aria-hidden size={11} /></button>}
            </li>
          ))}
        </ul>
        {!ro && (
          <div className="quality-inline-form">
            <label className="quality-sr" htmlFor="quality-licence-new">Add a licence</label>
            <input
              className="quality-input quality-mono"
              id="quality-licence-new"
              onChange={(e) => setNewLicence(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLicence(); } }}
              placeholder="SPDX id, e.g. MIT"
              value={newLicence}
            />
            <button className="quality-btn" onClick={addLicence} type="button"><Plus aria-hidden size={13} />Add</button>
          </div>
        )}
        <FieldError text={errorAt('licences')} />
      </section>

      <section aria-labelledby="quality-online-title" className="quality-card">
        <div className="quality-pad-head"><h2 id="quality-online-title">Online lookups</h2><p className="quality-muted">Only these two reach the internet; every other check runs in your sandbox.</p></div>
        <div className="quality-rule">
          <Switch aria-label="Known vulnerabilities (OSV)" checked={Boolean(draft.online?.vulnerabilities)} disabled={ro} onChange={(on) => set((p) => ({ ...p, online: { ...(p.online || {}), vulnerabilities: on } }))} size="small" />
          <span className="quality-grow"><strong>Known vulnerabilities</strong>: sends the names and versions of your packages (never your code) to OSV (api.osv.dev). Off, the Vulnerabilities check says it is offline.</span>
        </div>
        <div className="quality-rule">
          <Switch aria-label="Outdated packages (registry)" checked={Boolean(draft.online?.registry)} disabled={ro} onChange={(on) => set((p) => ({ ...p, online: { ...(p.online || {}), registry: on } }))} size="small" />
          <span className="quality-grow"><strong>Outdated packages</strong>: asks the npm registry and PyPI for the latest versions of your packages. Off by default: it is not a vulnerability check.</span>
        </div>
        <div className="quality-rule">
          <span className="quality-grow">
            Tests: time limit per service{' '}
            <input
              aria-invalid={Boolean(errorAt('tests.timeoutSeconds'))}
              aria-label="Tests time limit in seconds"
              className="quality-input quality-num"
              disabled={ro}
              max={1800}
              min={30}
              onChange={(e) => set((p) => ({ ...p, tests: { ...(p.tests || {}), timeoutSeconds: e.target.value === '' ? '' : Number(e.target.value) } }))}
              type="number"
              value={draft.tests?.timeoutSeconds ?? 600}
            />
            {' '}seconds (30 to 1800)
          </span>
          <FieldError text={errorAt('tests')} />
        </div>
      </section>

      <section aria-labelledby="quality-tools-title" className="quality-card">
        <div className="quality-pad-head is-row">
          <div><h2 id="quality-tools-title">Languages and tools</h2><p className="quality-muted">Detected from the code: what each check uses for each service.</p></div>
          <button className="quality-btn" disabled={detecting} onClick={runDetect} type="button">{detecting ? 'Detecting…' : 'Detect again'}</button>
        </div>
        <div className="quality-table-box is-flush">
          <DataTable
            className="quality-table"
            columns={TOOLS_COLUMNS}
            rowKey="service"
            rows={detected?.services || []}
            storageKey="flow.quality.tools.hiddenColumns"
            testId="quality-tools"
          />
          <dl className="quality-tools-notes quality-small" data-testid="quality-tools-notes">
            <div><dt>Whole project</dt><dd>Built into Flow: secrets, security rules (code, Dockerfiles, manifests), licences, the SBOM, production readiness, code stats, privacy and the API contract. Vulnerabilities: OSV. Code scanning: Semgrep (C#, Rust, PHP, Ruby) and Hadolint (Dockerfiles), installed when first needed.</dd></div>
            <div><dt>Running app</dt><dd>Accessibility: axe-core {headless?.available ? 'in a headless browser in your Flow' : 'in your browser'}. Performance: bundle sizes from the build, response times from Traces. Lighthouse: a headless browser in your Flow.</dd></div>
          </dl>
          {detected && !detected.services?.length && <p className="quality-muted quality-small quality-pad-foot">{detected.failed ? 'Could not detect the tools.' : 'No service found yet.'}</p>}
        </div>
      </section>

      <section aria-labelledby="quality-ignore-title" className="quality-card">
        <div className="quality-pad-head is-row">
          <div><h2 id="quality-ignore-title">Ignore rules</h2><p className="quality-muted">Every rule needs a reason and an end date. Expired rules stop working and show up in the overview.</p></div>
          {canEdit && !adding && <button className="quality-btn is-primary" onClick={() => setAdding(true)} type="button">Add a rule</button>}
        </div>
        <div className="quality-table-box is-flush">
          <DataTable
            actions={canEdit ? {
              label: 'Actions',
              width: 56,
              render: (rule) => <button aria-label={`Remove the rule for ${rule.what}`} className="quality-btn is-sm is-quiet" onClick={() => removeRule(rule.index)} type="button"><Trash aria-hidden size={13} /></button>,
            } : undefined}
            className="quality-table"
            columns={IGNORE_COLUMNS}
            empty={<span className="quality-muted">No ignore rules.</span>}
            rowKey={(rule) => `${rule.what}-${rule.index}`}
            rows={(draft.ignore || []).map((rule, index) => ({ ...rule, index }))}
            storageKey="flow.quality.ignores.hiddenColumns"
            testId="quality-ignores"
          />
        </div>
        {adding && <div className="quality-pad-foot is-ground"><IgnoreForm choices={findingChoices} onCancel={() => setAdding(false)} onSave={addRule} /></div>}
      </section>

      <QualityWaivers canEdit={canEdit} onChange={(waive) => set((prev) => ({ ...prev, waive }))} services={(detected?.services || []).map((s) => s.service)} waivers={draft.waive || []} />

      {(dirty || saveError || notice) && (
        <div className="quality-savebar" data-testid="quality-savebar" role="region" aria-label="Save the settings">
          <span className="quality-grow" role={saveError ? 'alert' : 'status'}>{saveError || (dirty ? 'Unsaved changes' : notice)}</span>
          {dirty && canEdit && (
            <>
              <button className="quality-btn" disabled={saving} onClick={() => { setDraft(answer.settings); setFieldErrors({}); setSaveError(null); }} type="button">Cancel</button>
              <button className="quality-btn is-primary" disabled={saving} onClick={save} type="button">{saving ? 'Saving…' : 'Save'}</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

QualitySettings.propTypes = {
  canEdit: PropTypes.bool,
  // Suggestions for the ignore form's What (the last run's rules and advisory ids).
  findingChoices: PropTypes.arrayOf(PropTypes.string),
  onBack: PropTypes.func.isRequired,
  // () => void: after anything was saved (the overview and the gate read again).
  onSaved: PropTypes.func,
  // The overview's `headless: { available, reason }`: accessibility can then run on every change too.
  headless: PropTypes.object,
  projectId: PropTypes.string.isRequired,
};
